import React, { useState, useEffect } from 'react';
import { Container, Alert } from '@mui/material';
import { SolicitationInput } from './SolicitationInput';
import { SolicitationResult } from './SolicitationResult';
import { useAuth } from '../../contexts/AuthContext';
import { SolicitationAnalysisResult, SolicitationData } from '../../types/solicitation';
import { solicitationService } from '../../services/SolicitationService';
import { userService } from '../../services/UserService';
import { PageHeader } from '../layout/PageHeader';

export const SolicitationPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<SolicitationAnalysisResult | null>(null);
  const [searchesRemaining, setSearchesRemaining] = useState<number | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    canSearch: boolean;
    reason?: string;
  } | null>(null);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!currentUser) return;
      
      try {
        const status = await userService.canPerformSearch(currentUser.uid);
        setSubscriptionStatus(status);
        if (status.searchesRemaining !== undefined) {
          setSearchesRemaining(status.searchesRemaining);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        setError('Failed to verify subscription status');
      }
    };

    checkSubscription();
  }, [currentUser]);

  const generateKeyHighlights = (data: SolicitationData): string[] => {
    const highlights: string[] = [];

    // Add set-aside information
    if (data.additionalInfo.setAside.type) {
      highlights.push(`Set-Aside: ${data.additionalInfo.setAside.type}`);
    }

    // Add budget information
    if (data.additionalInfo?.budget?.amount) {
      highlights.push(
        `Budget: $${data.additionalInfo.budget.amount.toLocaleString()} (${data.additionalInfo.budget.type || 'Not specified'})`
      );
    } else if (data.description?.includes('$')) {
      // Try to extract budget from description if it contains dollar amounts
      const budgetMatch = data.description.match(/\$[\d,]+(?:\s*(?:to|-)\s*\$[\d,]+)?(?:\s*(?:million|M|k|K|thousand))?/);
      if (budgetMatch) {
        highlights.push(`Estimated Value: ${budgetMatch[0]}`);
      }
    }

    // Add response deadline
    if (data.timeline?.responseDate && !isNaN(data.timeline.responseDate.getTime())) {
      highlights.push(
        `Response Due: ${data.timeline.responseDate.toLocaleDateString()}`
      );
    }

    // Add performance period
    if (data.timeline?.performancePeriod) {
      highlights.push(`Performance Period: ${data.timeline.performancePeriod}`);
    }

    // Add NAICS codes
    const primaryNaics = data.technicalDetails?.naicsCodes?.find(code => code.primary);
    if (primaryNaics?.code) {
      highlights.push(`Primary NAICS: ${primaryNaics.code}${primaryNaics.description ? ` - ${primaryNaics.description}` : ''}`);
    }

    // Add location information
    if (data.location?.placeOfPerformance) {
      const locationParts = [
        data.location.placeOfPerformance.city,
        data.location.placeOfPerformance.state,
        data.location.placeOfPerformance.country
      ].filter(Boolean);
      
      if (locationParts.length > 0) {
        highlights.push(`Location: ${locationParts.join(', ')}`);
      }
    }

    return highlights;
  };

  const handleSolicitationSubmit = async (url: string) => {
    if (!currentUser) {
      setError('You must be logged in to analyze solicitations');
      return;
    }

    // Check subscription status
    const status = await userService.canPerformSearch(currentUser.uid);
    if (!status.canSearch) {
      setError(status.reason || 'Subscription check failed');
      return;
    }

    setIsLoading(true);
    setError(null);
    setAnalysisResult(null);

    try {
      // Validate URL
      if (!solicitationService.isValidSAMUrl(url)) {
        throw new Error('Invalid SAM.gov URL format');
      }

      // Increment search count before making the request
      await userService.incrementSearchCount(currentUser.uid);

      // Fetch solicitation data
      const solicitationData = await solicitationService.fetchSolicitationData(currentUser.uid, url);

      // Update searches remaining
      const newStatus = await userService.canPerformSearch(currentUser.uid);
      setSubscriptionStatus(newStatus);
      if (newStatus.searchesRemaining !== undefined) {
        setSearchesRemaining(newStatus.searchesRemaining);
      }

      const result: SolicitationAnalysisResult = {
        data: solicitationData,
        aiSummary: 'AI summary generation coming soon...', // Will be replaced with actual AI integration
        keyHighlights: generateKeyHighlights(solicitationData),
        recommendedActions: [
          'Review technical requirements and certifications',
          'Assess compliance with set-aside requirements',
          'Prepare past performance documentation',
          'Contact the contracting officer for clarifications',
          'Begin proposal preparation planning'
        ],
        riskAssessment: {
          level: 'medium',
          factors: [
            {
              factor: 'Timeline',
              impact: solicitationData.timeline.responseDate && !isNaN(solicitationData.timeline.responseDate.getTime())
                ? `Response due in ${Math.ceil(
                    (solicitationData.timeline.responseDate.getTime() - new Date().getTime()) / 
                    (1000 * 60 * 60 * 24)
                  )} days`
                : 'Response date not available',
              mitigation: 'Begin preparation immediately'
            }
          ]
        }
      };

      // Save to Firestore
      await solicitationService.saveSolicitationData(currentUser.uid, solicitationData);

      setAnalysisResult(result);
    } catch (error) {
      console.error('Error analyzing solicitation:', error);
      
      // Handle specific error types
      if (error instanceof Error) {
        if (error.message.includes('unauthenticated')) {
          setError('Your session has expired. Please log in again.');
        } else if (error.message.includes('quota')) {
          setError('You have exceeded your quota. Please upgrade your subscription.');
        } else if (error.message.includes('permission-denied')) {
          setError('You do not have permission to access this feature.');
        } else {
          setError(`Failed to analyze solicitation: ${error.message}`);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }

      // Rollback search count increment if the analysis failed
      try {
        if (currentUser) {
          const userData = await userService.getUserData(currentUser.uid);
          if (userData && userData.usage.searchCount > 0) {
            await userService.resetSearchCount(currentUser.uid);
          }
        }
      } catch (rollbackError) {
        console.error('Error rolling back search count:', rollbackError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusMessage = () => {
    if (searchesRemaining !== null) {
      return `${searchesRemaining} searches remaining this month`;
    }
    if (subscriptionStatus && !subscriptionStatus.canSearch) {
      return subscriptionStatus.reason;
    }
    return null;
  };

  const statusMessage = getStatusMessage();

  return (
    <>
      <PageHeader 
        title="Analyze Solicitation"
        description={
          statusMessage || 
          "Enter a SAM.gov opportunity URL to analyze the solicitation details and get AI-powered insights"
        }
      />
      <Container maxWidth="lg">
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <SolicitationInput
          onSubmit={handleSolicitationSubmit}
          isLoading={isLoading}
        />

        {analysisResult && (
          <SolicitationResult result={analysisResult} />
        )}
      </Container>
    </>
  );
};
